import type { CSSProperties, HTMLAttributes } from "react";

import {
  resolveFunctionalSymbol,
  type FunctionalSymbolSemantic,
} from "./registry";

type SymbolStyle = CSSProperties & {
  "--quiddity-symbol-fill"?: number;
  "--quiddity-symbol-grade"?: number;
  "--quiddity-symbol-optical-size"?: number;
  "--quiddity-symbol-size"?: string;
  "--quiddity-symbol-weight"?: number;
};

export interface FunctionalSymbolProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /** Stable product meaning; the Material glyph remains an implementation detail. */
  semantic: FunctionalSymbolSemantic;
  /** Overrides the registry's accessible label for a more specific context. */
  label?: string;
  /** Keep true for a visible text fallback if the font cannot render. */
  showLabel?: boolean;
  /** Hides the entire symbol from assistive technology when adjacent copy repeats it. */
  decorative?: boolean;
  size?: number | string;
  fill?: boolean;
  weight?: number;
  grade?: number;
  opticalSize?: number;
}

export function FunctionalSymbol({
  semantic,
  label,
  showLabel = true,
  decorative = false,
  size = "1em",
  fill = false,
  weight = 400,
  grade = 0,
  opticalSize = 24,
  className = "",
  style,
  ...rest
}: FunctionalSymbolProps) {
  const definition = resolveFunctionalSymbol(semantic);
  const accessibleLabel = label ?? definition?.accessibleLabel ?? "Unavailable action";
  const visibleLabel = label ?? definition?.fallbackText ?? "Unavailable action";
  const resolvedStyle: SymbolStyle = {
    "--quiddity-symbol-fill": fill ? 1 : 0,
    "--quiddity-symbol-grade": grade,
    "--quiddity-symbol-optical-size": opticalSize,
    "--quiddity-symbol-size": typeof size === "number" ? `${size}px` : size,
    "--quiddity-symbol-weight": weight,
    ...style,
  };

  if (!definition) {
    return (
      <span
        {...rest}
        className={`quiddity-functional-symbol quiddity-functional-symbol--fallback ${className}`.trim()}
        data-semantic={semantic}
        style={resolvedStyle}
      >
        {visibleLabel}
      </span>
    );
  }

  return (
    <span
      {...rest}
      aria-hidden={decorative ? "true" : undefined}
      className={`quiddity-functional-symbol ${className}`.trim()}
      data-semantic={semantic}
      data-symbol={definition.symbol}
      style={resolvedStyle}
    >
      <span className="quiddity-functional-symbol__glyph" aria-hidden="true">
        {String.fromCodePoint(Number.parseInt(definition.codepoint, 16))}
      </span>
      {showLabel ? (
        <span className="quiddity-functional-symbol__label">{visibleLabel}</span>
      ) : decorative ? null : (
        <span className="quiddity-functional-symbol__sr-only">{accessibleLabel}</span>
      )}
    </span>
  );
}
